import React, { useState, useEffect } from "react";
import { Modal, Button } from "react-bootstrap";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { DatabaseService } from "../../services/DatabaseService";

// Deployment stages configuration - mapped to K8s status
const DEPLOYMENT_STAGES = [
    { key: "initiating", label: "Initiating Deployment", icon: "rocket", k8sStatus: ["pending", "creating"] },
    { key: "creating", label: "Creating Resources", icon: "server", k8sStatus: ["pending"] },
    { key: "provisioning", label: "Provisioning Pod", icon: "cube", k8sStatus: ["starting", "pending"] },
    { key: "configuring", label: "Configuring Database", icon: "database", k8sStatus: ["starting"] },
    { key: "networking", label: "Setting Up Network", icon: "globe", k8sStatus: ["starting"] },
    { key: "ready", label: "Database Ready!", icon: "check", k8sStatus: ["running"] },
];

// Stage Icon Component
const StageIcon = ({ icon, status }) => {
    const icons = {
        rocket: (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 00-2.91-.09z"/>
                <path d="M12 15l-3-3a22 22 0 012-3.95A12.88 12.88 0 0122 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 01-4 2z"/>
                <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0"/>
                <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5"/>
            </svg>
        ),
        server: (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="2" y="2" width="20" height="8" rx="2" ry="2"/>
                <rect x="2" y="14" width="20" height="8" rx="2" ry="2"/>
                <line x1="6" y1="6" x2="6.01" y2="6"/>
                <line x1="6" y1="18" x2="6.01" y2="18"/>
            </svg>
        ),
        cube: (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/>
                <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
                <line x1="12" y1="22.08" x2="12" y2="12"/>
            </svg>
        ),
        database: (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <ellipse cx="12" cy="5" rx="9" ry="3"/>
                <path d="M21 12c0 1.66-4.03 3-9 3s-9-1.34-9-3"/>
                <path d="M3 5v14c0 1.66 4.03 3 9 3s9-1.34 9-3V5"/>
            </svg>
        ),
        globe: (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <line x1="2" y1="12" x2="22" y2="12"/>
                <path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/>
            </svg>
        ),
        check: (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="20 6 9 17 4 12"/>
            </svg>
        ),
    };

    return (
        <div className={`deploy-stage-icon ${status}`}>
            {icons[icon] || icons.server}
        </div>
    );
};

// Circular Progress Component
const CircularProgress = ({ value, size = 120, strokeWidth = 8 }) => {
    const radius = (size - strokeWidth) / 2;
    const circumference = radius * 2 * Math.PI;
    const offset = circumference - (value / 100) * circumference;

    const getColor = () => {
        if (value >= 100) return "#10b981"; // Green
        if (value >= 50) return "#2b8dc8"; // Blue
        return "#2b8dc8"; // Blue
    };

    return (
        <div className="circular-progress-container">
            <svg width={size} height={size} className="circular-progress">
                {/* Background circle */}
                <circle
                    className="circular-bg"
                    stroke="#e4e4e4"
                    fill="transparent"
                    strokeWidth={strokeWidth}
                    r={radius}
                    cx={size / 2}
                    cy={size / 2}
                />
                {/* Progress circle */}
                <circle
                    className="circular-bar"
                    stroke={getColor()}
                    fill="transparent"
                    strokeWidth={strokeWidth}
                    strokeLinecap="round"
                    strokeDasharray={circumference}
                    strokeDashoffset={offset}
                    r={radius}
                    cx={size / 2}
                    cy={size / 2}
                    style={{
                        transform: "rotate(-90deg)",
                        transformOrigin: "50% 50%",
                        transition: "stroke-dashoffset 0.5s ease",
                    }}
                />
            </svg>
            <div className="circular-progress-value">
                <span className="progress-number">{value}</span>
                <span className="progress-percent">%</span>
            </div>
        </div>
    );
};

function DeploymentProgressModal({ show, onHide, databaseId, databaseName, onComplete, onViewDatabase }) {
    const navigate = useNavigate();
    const [currentStage, setCurrentStage] = useState(0);
    const [progress, setProgress] = useState(0);
    const [status, setStatus] = useState("deploying"); // deploying, success, error
    const [message, setMessage] = useState("Initiating deployment...");
    // eslint-disable-next-line no-unused-vars
    const [stageDetails, setStageDetails] = useState(null);
    
    // Configuration
    const POLL_INTERVAL_MS = 3000; // Poll every 3 seconds

    // Poll deployment stage from API - this is the ONLY source of truth
    useEffect(() => {
        if (!show || !databaseId || status === "success" || status === "error") return;

        let isMounted = true;
        let maxProgress = 0; // Track max to prevent backward progress

        const pollDeploymentStage = async () => {
            if (!isMounted) return;
            
            try {
                // Use the new deployment-stage API which returns structured stage info
                const response = await DatabaseService.getDeploymentStage(databaseId);
                const data = response?.data?.data || response?.data;
                
                if (!isMounted) return;
                
       
                
                if (data) {
                    // Use the backend's calculated progress/stage
                    const stageIndex = data.currentStageIndex || 1;
                    const progressPercent = data.progressPercent || 0;
                    const isComplete = data.isComplete || false;
                    const isFailed = data.isFailed || false;
                    const stageMessage = data.message || "Processing...";
                    
                    // Store full stage details
                    setStageDetails(data);
                    
                    // Check for failure
                    if (isFailed) {
                        setStatus("error");
                        setMessage(data.error || stageMessage || "Deployment failed");
                        setProgress(maxProgress);
                        return;
                    }
                    
                    // Check for completion
                    if (isComplete) {
             
                        setStatus("success");
                        setProgress(100);
                        setCurrentStage(5);
                        setMessage("Database deployed successfully!");
                        toast.success("🎉 Database deployed successfully!");
                        
                        if (onComplete) {
                            onComplete({ id: databaseId, name: databaseName });
                        }
                        return;
                    }
                    
                    // Update progress (never go backward)
                    maxProgress = Math.max(maxProgress, progressPercent);
                    
                    // Map stage index to our stage (0-5)
                    // Backend: 1=initiating, 2=creating, 3=provisioning, 4=configuring, 5=networking, 6=ready
                    const frontendStage = Math.max(0, stageIndex - 1);
                    
                    setCurrentStage(frontendStage);
                    setProgress(maxProgress);
                    setMessage(stageMessage);
                }
            } catch (error) {
                console.error("Error polling deployment stage:", error);
                // Don't fail on polling errors, just continue
            }
        };

        // Initial poll after small delay
        const initialTimeout = setTimeout(pollDeploymentStage, 1000);
        
        // Poll every 3 seconds
        const interval = setInterval(pollDeploymentStage, POLL_INTERVAL_MS);

        // Timeout after 10 minutes
        const timeout = setTimeout(() => {
            if (isMounted && status === "deploying") {
                setStatus("error");
                setMessage("Deployment timed out. Please check the database status manually.");
            }
        }, 600000);

        return () => {
            isMounted = false;
            clearTimeout(initialTimeout);
            clearInterval(interval);
            clearTimeout(timeout);
        };
    }, [show, databaseId, status, databaseName, onComplete]);

    // Reset state when modal opens
    useEffect(() => {
        if (show) {
            setCurrentStage(0);
            setProgress(0);
            setStatus("deploying");
            setMessage("Initiating deployment...");
            setStageDetails(null);
        }
    }, [show]);

    // Handle navigation to database detail
    const handleGoToDatabase = () => {
        onHide();
        if (databaseId) {
            navigate(`/database/${databaseId}`);
        } else if (onViewDatabase) {
            onViewDatabase();
        }
    };

    // Handle navigation to database list
    const handleGoToList = () => {
        onHide();
        navigate("/database");
    };

    const getStageStatus = (index) => {
        if (status === "success") return "completed";
        if (index < currentStage) return "completed";
        if (index === currentStage) return "active";
        return "pending";
    };

    return (
        <Modal
            show={show}
            onHide={status === "deploying" ? null : onHide}
            centered
            backdrop="static"
            keyboard={false}
            className="deployment-progress-modal"
            size="lg"
        >
            <Modal.Body className="deployment-modal-body">
                {/* Header */}
                <div className="deploy-modal-header">
                    <div className={`deploy-status-icon ${status}`}>
                        {status === "deploying" ? (
                            <div className="deploy-spinner"></div>
                        ) : status === "success" ? (
                            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                <polyline points="20 6 9 17 4 12"/>
                            </svg>
                        ) : (
                            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="12" cy="12" r="10"/>
                                <line x1="15" y1="9" x2="9" y2="15"/>
                                <line x1="9" y1="9" x2="15" y2="15"/>
                            </svg>
                        )}
                    </div>
                    <div className="deploy-header-text">
                        <h4>
                            {status === "deploying"
                                ? "Deploying Database"
                                : status === "success"
                                ? "Deployment Complete!"
                                : "Deployment Failed"}
                        </h4>
                        <p>{databaseName}</p>
                    </div>
                </div>

                {/* Progress Section */}
                <div className="deploy-progress-section">
                    <CircularProgress value={progress} />
                    <div className="deploy-message">{message}</div>
                </div>

                {/* Stages Timeline */}
                <div className="deploy-stages-timeline">
                    <div className="deploy-stages-timeline-inner">
                        {DEPLOYMENT_STAGES.map((stage, index) => (
                            <div key={stage.key} className={`deploy-stage-item ${getStageStatus(index)}`}>
                                <StageIcon icon={stage.icon} status={getStageStatus(index)} />
                                <div className="deploy-stage-content">
                                    <span className="stage-label">{stage.label}</span>
                                    {getStageStatus(index) === "active" && (
                                        <span className="stage-status">In Progress...</span>
                                    )}
                                    {getStageStatus(index) === "completed" && (
                                        <span className="stage-status completed">Done</span>
                                    )}
                                </div>
                                {index < DEPLOYMENT_STAGES.length - 1 && (
                                    <div className={`stage-connector ${getStageStatus(index) === "completed" ? "completed" : ""}`} />
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                {/* Connection Info (shown on success) */}
                {status === "success" && (
                    <div className="deploy-success-message">
                        <p className="success-text">
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                <circle cx="12" cy="12" r="10"/>
                                <polyline points="16 10 11 15 8 12"/>
                            </svg>
                            Your database is ready! View connection details on the database page.
                        </p>
                    </div>
                )}

                {/* Actions */}
                <div className="deploy-modal-actions">
                    {status === "deploying" ? (
                        <p className="deploy-warning-text">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="12" cy="12" r="10"/>
                                <line x1="12" y1="8" x2="12" y2="12"/>
                                <line x1="12" y1="16" x2="12.01" y2="16"/>
                            </svg>
                            Please wait while we set up your database. This may take 2-5 minutes.
                        </p>
                    ) : status === "success" ? (
                        <>
                            <Button variant="outline-secondary" onClick={handleGoToList}>
                                View All Databases
                            </Button>
                            <Button variant="primary" onClick={handleGoToDatabase}>
                                View Database
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button variant="outline-secondary" onClick={onHide}>
                                Close
                            </Button>
                            <Button variant="primary" onClick={handleGoToList}>
                                View All Databases
                            </Button>
                        </>
                    )}
                </div>
            </Modal.Body>
        </Modal>
    );
}

export default DeploymentProgressModal;
